import { Input, Table, TableContainer, Tbody, Td, Text, Tfoot, Th, Thead, Tr } from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'

import { ActionButton } from '../../../components/ActionButton'
import { EmptyCell } from '../../../components/EmptyCell'
import { FILTER_CONTROL_SIZE, FilterBar, FilterField, FilterSelect } from '../../../components/FilterBar'
import { Icon } from '../../../components/Icon'
import { TableSkeletonRows } from '../../../components/Loading'
import { PageTitle } from '../../../components/PageTitle'
import PaymentDateModal from '../../../components/PaymentDateModal'
import { StatusBadge } from '../../../components/StatusBadge'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { formatCurrency, formatIsoDate, useAppToast, useHasRole } from '../../../helpers'
import EventCommissionService from '../../../services/EventCommissions/EventCommissionService'
import {
  CommissionTypeFilter,
  IEventCommissionSummaryFilter,
  IEventCommissionSummaryItem,
  IEventCommissionSummaryTotals,
  PaymentStatusFilter
} from '../../../services/EventCommissions/interfaces'
import { commissionTypePluralLabels } from '../EventCoupons/couponLabels'
import {
  beneficiaryKey,
  beneficiaryLabel,
  beneficiaryScope,
  canOpenSales,
  COMMISSION_PAYMENT_STATUS,
  paymentStatus,
  sellerDisplayName
} from './commissionDisplay'
import CommissionPaymentUndoDialog from './CommissionPaymentUndoDialog'
import PartnerContactModal from './PartnerContactModal'
import SellerSalesModal from './SellerSalesModal'

const emptyTotals: IEventCommissionSummaryTotals = {
  ticket_count: 0,
  total_sales_value: 0,
  commission_value: 0,
  paid_commission_value: 0,
  pending_commission_value: 0
}

const INITIAL_FILTERS: IEventCommissionSummaryFilter = { month: '', commission_type: '', payment_status: '' }

const COLUMN_COUNT = 5

const typeFilterOptions: { value: CommissionTypeFilter; label: string }[] = [
  { value: 'teacher', label: commissionTypePluralLabels.teacher },
  { value: 'partner', label: commissionTypePluralLabels.partner },
  { value: 'commercial', label: commissionTypePluralLabels.commercial }
]

const paymentFilterOptions: { value: PaymentStatusFilter; label: string }[] = [
  { value: 'pending', label: 'Pendente' },
  { value: 'paid', label: 'Pago' }
]

export default function EventCommissionsPage() {
  const toast = useAppToast()
  const hasRole = useHasRole()
  const service = useMemo(() => new EventCommissionService(), [])
  const permissions = { payCommissions: hasRole('pay_event_commissions') }

  const [rows, setRows] = useState<IEventCommissionSummaryItem[]>([])
  const [totals, setTotals] = useState<IEventCommissionSummaryTotals>(emptyTotals)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [filters, setFilters] = useState<IEventCommissionSummaryFilter>(INITIAL_FILTERS)
  const isDirty = filters.month !== '' || filters.commission_type !== '' || filters.payment_status !== ''
  // Guarda a linha inteira: o modal escopa as vendas pelo beneficiário dela.
  const [salesOf, setSalesOf] = useState<IEventCommissionSummaryItem | null>(null)
  const [contactOf, setContactOf] = useState<IEventCommissionSummaryItem | null>(null)
  const [payingRow, setPayingRow] = useState<IEventCommissionSummaryItem | null>(null)
  const [undoingRow, setUndoingRow] = useState<IEventCommissionSummaryItem | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  const fetchSummary = async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const data = await service.listSummary(filters)
      setRows(data.data)
      setTotals(data.totals)
    } catch (error) {
      setLoadError(true)
      toast.error('Erro ao carregar a comissão', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const delayDebounce = setTimeout(() => fetchSummary(), 400)
    return () => clearTimeout(delayDebounce)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters])

  /**
   * O escopo do pagamento é o beneficiário da linha mais os filtros de período em vigor: o
   * backend quita exatamente as vendas que estão somadas naquela linha, nem uma a mais.
   */
  const paymentScope = (row: IEventCommissionSummaryItem): IEventCommissionSummaryFilter => ({
    month: filters.month,
    ...beneficiaryScope(row)
  })

  const confirmPayment = async (date: string) => {
    if (!payingRow) return

    setIsSaving(true)
    try {
      const result = await service.markPaid(paymentScope(payingRow), date)
      toast.success(
        'Pagamento registrado',
        `${result.marked} venda${result.marked === 1 ? '' : 's'} — ${formatCurrency(result.total_value)} em ${formatIsoDate(date)}.`
      )
      setPayingRow(null)
      fetchSummary()
    } catch (error) {
      toast.error('Erro ao registrar o pagamento', error)
    } finally {
      setIsSaving(false)
    }
  }

  /** `undefined` deixa o rótulo padrão da ação valer; os outros explicam por que ela está travada. */
  const payActionLabel = (row: IEventCommissionSummaryItem) => {
    if (paymentStatus(row) === 'none') return 'Esta linha não tem comissão a pagar'
    if (!canOpenSales(row)) return 'Parceiro sem nome — não dá para escopar o pagamento'

    return row.pending_sales_count === 0 ? 'Tudo pago nesta linha' : undefined
  }

  return (
    <>
      <PageTitle title="Comissão de Eventos" description="Comissões apuradas por consultor em cada evento" mb={6} />

      <FilterBar onClear={() => setFilters(INITIAL_FILTERS)} isDirty={isDirty}>
        <FilterField label="Mês" maxW="180px">
          <Input
            type="month"
            size={FILTER_CONTROL_SIZE}
            value={filters.month ?? ''}
            onChange={(e) => setFilters((f) => ({ ...f, month: e.target.value }))}
          />
        </FilterField>
        <FilterSelect
          label="Tipo"
          maxW="200px"
          value={filters.commission_type ?? ''}
          onChange={(value) => setFilters((f) => ({ ...f, commission_type: value as CommissionTypeFilter }))}
          options={typeFilterOptions}
        />
        <FilterSelect
          label="Pagamento"
          maxW="180px"
          value={filters.payment_status ?? ''}
          onChange={(value) => setFilters((f) => ({ ...f, payment_status: value as PaymentStatusFilter }))}
          options={paymentFilterOptions}
        />
      </FilterBar>

      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>Nome do vendedor</Th>
              <Th isNumeric>Quantidade de ingressos</Th>
              <Th isNumeric>Valor da comissão</Th>
              <Th>Pagamento</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={COLUMN_COUNT} />
            ) : loadError ? (
              <TableErrorRow colSpan={COLUMN_COUNT} entity="comissões" feminine onRetry={fetchSummary} />
            ) : rows.length === 0 ? (
              <TableEmptyRow colSpan={COLUMN_COUNT} message="Nenhuma comissão no período" />
            ) : (
              rows.map((row) => {
                const status = paymentStatus(row)

                return (
                  <Tr key={beneficiaryKey(row)}>
                    <Td>
                      <Text fontWeight="500" color="gray.800">
                        {sellerDisplayName(row)}
                      </Text>
                      <Text variant="caption">{beneficiaryLabel(row)}</Text>
                    </Td>
                    <Td isNumeric>{row.ticket_count}</Td>
                    <Td isNumeric>
                      {/* Sem comissionamento configurado não há nada a pagar: só volume. */}
                      <Text color={row.commission_value === null ? 'gray.400' : 'gray.700'}>
                        <EmptyCell
                          value={row.commission_value === null ? null : formatCurrency(row.commission_value)}
                        />
                      </Text>
                      <Text variant="caption">{formatCurrency(row.total_sales_value)} vendidos</Text>
                    </Td>
                    <Td>
                      {status === 'none' ? (
                        <EmptyCell />
                      ) : (
                        <>
                          <StatusBadge status={status} statuses={COMMISSION_PAYMENT_STATUS} />
                          <Text variant="caption" mt={1}>
                            {/* Pago mostra a data; o resto mostra o que ainda falta sair. Linha
                                quitada nunca exibe "a pagar", nem se a data vier vazia. */}
                            {status === 'paid'
                              ? row.last_paid_on && `em ${formatIsoDate(row.last_paid_on)}`
                              : `${formatCurrency(row.pending_commission_value ?? 0)} a pagar`}
                          </Text>
                        </>
                      )}
                    </Td>
                    <Td>
                      <ActionButton
                        action="view"
                        label={canOpenSales(row) ? 'Ver vendas' : 'Parceiro sem nome — não dá para escopar as vendas'}
                        isDisabled={!canOpenSales(row)}
                        onClick={() => setSalesOf(row)}
                      />
                      {/* Só parceiro: os outros beneficiários são pagos pelo cadastro deles. */}
                      {row.commission_type === 'partner' && (
                        <ActionButton
                          action="view"
                          icon={<Icon name="lni lni-wallet" />}
                          label={
                            row.partner_contact
                              ? 'Dados para pagamento'
                              : 'Nenhum dado de pagamento cadastrado no cupom'
                          }
                          isDisabled={!row.partner_contact}
                          onClick={() => setContactOf(row)}
                        />
                      )}
                      {permissions.payCommissions && (
                        <ActionButton
                          action="mark-paid"
                          label={payActionLabel(row)}
                          isDisabled={row.pending_sales_count === 0 || !canOpenSales(row)}
                          onClick={() => setPayingRow(row)}
                        />
                      )}
                      {/* Mesma guarda do botão de pagar: sem o nome do parceiro o escopo não fecha
                          e o backend recusa a chamada com 422. */}
                      {permissions.payCommissions && row.paid_sales_count > 0 && canOpenSales(row) && (
                        <ActionButton action="unmark-paid" onClick={() => setUndoingRow(row)} />
                      )}
                    </Td>
                  </Tr>
                )
              })
            )}
          </Tbody>
          {/* Totais em Tfoot, como o resumo de prêmios de Comprovantes de Vendas: a linha é do
              período inteiro, não um registro da listagem, e assim não some junto com as linhas. */}
          {!loading && rows.length > 0 && (
            <Tfoot>
              <Tr>
                <Th>Total</Th>
                <Th isNumeric>{totals.ticket_count}</Th>
                <Th isNumeric>{formatCurrency(totals.commission_value)}</Th>
                <Th>
                  <Text fontSize="xs" color="gray.500" textTransform="none">
                    {formatCurrency(totals.paid_commission_value)} pagos
                  </Text>
                  <Text fontSize="xs" color="gray.500" textTransform="none">
                    {formatCurrency(totals.pending_commission_value)} a pagar
                  </Text>
                </Th>
                <Th />
              </Tr>
            </Tfoot>
          )}
        </Table>
      </TableContainer>

      {salesOf && (
        <SellerSalesModal
          isOpen={!!salesOf}
          onClose={() => setSalesOf(null)}
          seller={salesOf}
          initialMonth={filters.month ?? ''}
          canPay={permissions.payCommissions}
          onPaymentsChanged={fetchSummary}
        />
      )}

      {contactOf && <PartnerContactModal isOpen={!!contactOf} onClose={() => setContactOf(null)} seller={contactOf} />}

      {payingRow && (
        <PaymentDateModal
          isOpen={!!payingRow}
          onClose={() => setPayingRow(null)}
          onConfirm={confirmPayment}
          isLoading={isSaving}
          closeOnConfirm={false}
          title={`Pagar ${sellerDisplayName(payingRow)}`}
          description={`${payingRow.pending_sales_count} venda${payingRow.pending_sales_count === 1 ? '' : 's'} em aberto, ${formatCurrency(payingRow.pending_commission_value ?? 0)}. Escolha a data em que o pagamento foi realizado:`}
        />
      )}

      {undoingRow && (
        <CommissionPaymentUndoDialog
          isOpen={!!undoingRow}
          onClose={() => setUndoingRow(null)}
          description={`${undoingRow.paid_sales_count} venda${undoingRow.paid_sales_count === 1 ? '' : 's'} de ${sellerDisplayName(undoingRow)} (${formatCurrency(undoingRow.paid_commission_value ?? 0)}) ${undoingRow.paid_sales_count === 1 ? 'voltará' : 'voltarão'} para pendente. Tem certeza?`}
          // Mesmo escopo usado para pagar: desfazer alcança exatamente as vendas quitadas ali.
          onConfirm={() => service.unmarkPaid(paymentScope(undoingRow))}
          onUndone={fetchSummary}
        />
      )}
    </>
  )
}
